import { allDiagnoses } from 'Routes/Diagnosis/utils/definitions';
import { INeuroDocument } from 'neuro-data-structures';
import { partialDateToValue } from 'neuro-utils';
import { fieldNameToCodeString } from 'Routes/Background/utils';
import { path } from 'ramda';
import { sourceIsIntegration } from 'Utility/documentHandling';

/**
 * Usable documents for the user. Documents with no finished commits and not created by current user should not be returned
 * @param {Pick<IJWTBody, 'useruuid'>} jwt - JWT
 * @param {INeuroDocument[]} documents - INeuroDocument[]
 * @returns {INeuroDocument[]} Usable docs
 */
export const filterUsableDocuments = (
  jwt: Pick<IJWTBody, 'useruuid'>,
  documents: INeuroDocument[],
): INeuroDocument[] => {
  const validDocs: INeuroDocument[] = [];

  documents.forEach(doc => {
    if (doc.commits.some(c => c.commitDate !== null) || doc.creatorId === jwt.useruuid) {
      // If document has zero finished commits or it is not created by current user, discard it (we do not want to show empty documents)
      validDocs.push(doc);
    }
  });

  return validDocs;
};

/**
 * Usable and deleted documents for the user
 * @param {INeuroDocument[]} documents - INeuroDocument[]
 * @returns {[INeuroDocument[], INeuroDocument[]]} Usable docs and removed docs
 */
export const filterRemovedDocuments = (documents: INeuroDocument[]): [INeuroDocument[], INeuroDocument[]] => {
  const validDocs: INeuroDocument[] = [];
  const removedDocs: INeuroDocument[] = [];

  documents.forEach(doc => {
    // Put into removed docs ones that have a removal timestamp and at least some finished commits
    if (doc.removeTS !== null) {
      // Dont add documents deleted when cancelling creation
      if (path(['removeInfo', 'removeReason', 'reason'], doc) !== 'Document creation cancelled.') removedDocs.push(doc);
    } else validDocs.push(doc);
  });

  return [validDocs, removedDocs];
};

/**
 * Filter unfinished commits made by other users
 * @param {Pick<IJWTBody, 'useruuid'>} jwt - JWT
 * @param {INeuroDocument[]} documents - INeuroDocuments array
 * @returns {INeuroDocument[]} Filtered documents
 */
export const filterCommits = (jwt: Pick<IJWTBody, 'useruuid'>, documents: INeuroDocument[]): INeuroDocument[] => {
  // Filter away unclosed commits by other users
  const docsWithFilteredCommits = documents
    .map(d => ({
      ...d,
      commits: d.commits.filter(c => (!c.commitDate ? c.creatorId === jwt.useruuid : true)),
    }))
    .filter(d => d.commits.length > 0);

  return docsWithFilteredCommits;
};

/**
 * Filter diagnosis documents
 * All platforms: Filter if diagnosis code not included in allDiagnoses list
 * @param {INeuroDocument[]} documents - INeuroDocuments array
 * @returns {INeuroDocument[]} Filtered documents
 */
export const filterDiagnoses = <T extends IControlProps>(
  documents: INeuroDocument[],
  sortedAndMergedDocuments: Array<T>,
): INeuroDocument[] => {
  // Filter unwanted documents, such as ones from integration that are irrelevant
  const filteredDocuments = documents.filter(d => {
    if (d.documentType === 'diagnosis') {
      const mergedDocument: IDiagnosis | undefined = sortedAndMergedDocuments.find(s => s._id === d.documentId);
      // Filter diagnosis documents containing unsupported diagnosis code
      if (mergedDocument?.diagnosis && !allDiagnoses.includes(mergedDocument.diagnosis)) {
        return false;
      }
      return true;
    }
    return true;
  });
  return filteredDocuments;
};

/**
 * Compare two medications if they are equal. Primarily compare atc codes, but also compare medication names
 * @param {string} med1 - First medication to compare
 * @param {string} med2 - Second medication to compare
 * @param {IOrganizationNeuroSettings['medicationSettings']} medicationSettings - Medication settings
 * @returns {boolean} - true if medications are equal, false if not
 */
export const compareATCCodesAndName = (
  med1?: IMedication,
  med2?: IMedication,
  medicationSettings?: IOrganizationNeuroSettings['medicationSettings'],
): boolean => {
  if (!med1 || !med2) return false;

  // Return true if both ATC codes are equal
  if (med1.atc === med2.atc) return true;

  // Compare MS medication names from org settings
  const getMSMedication = (code?: string) =>
    code && medicationSettings?.ms?.medications?.find(med => med.atcCodes.includes(code));
  const msMed1 = getMSMedication(med1.atc);
  const msMed2 = getMSMedication(med2.atc);
  if (msMed1 && msMed2 && 'name' in msMed1 && 'name' in msMed2) return msMed1.name === msMed2.name;

  const medicationName1 = med1 && 'medicationName' in med1 ? med1.medicationName : undefined;
  const medicationName2 = med2 && 'medicationName' in med2 ? med2.medicationName : undefined;

  return !!(medicationName1 && medicationName2 && medicationName1 === medicationName2);
};

/**
 * Filter overlapping medication documents
 * Filter overlapping medications when they are the same medication based on atc or name. Preferring manually entered medications
 * Medications with a source that is not 'x-x-integration' should be treated the same as manually entered documents
 * @param {IMedication[]} medicationDocuments - Medication documents
 * @returns {IMedication[]} Filtered medications
 */
export const filterMedicationsOverlapping = (
  medicationDocuments: IMedication[],
  medicationSettings?: IOrgSettings['medicationSettings'],
): IMedication[] => {
  if (medicationDocuments.length === 0) return medicationDocuments;

  const sortedMeds = medicationDocuments.toSorted((m1, m2) => {
    const m1IsIntegration = sourceIsIntegration(m1._source);
    const m2IsIntegration = sourceIsIntegration(m2._source);

    // Sort medications so that integration medications are last in the list
    if (m1IsIntegration && m2IsIntegration) return 0;
    if (!m1IsIntegration && !m2IsIntegration) return 0;
    if (!m1IsIntegration && m2IsIntegration) return -1;
    if (m1IsIntegration && !m2IsIntegration) return 1;
    return 0;
  });

  const returnMeds: IMedication[] = [];

  sortedMeds.forEach((thisMedication: IMedication) => {
    /**
     * Add medications to returnMeds as long they don't overlap with existing medications in returnMeds
     * Medications are sorted so that manually entered meds added first (the document has no _source field)
     */

    // Get medications that can be filtered against (same atc or name)
    const filteringMeds = returnMeds.filter(f => compareATCCodesAndName(f, thisMedication, medicationSettings));
    const thisInReturnMeds = returnMeds.find(rm => compareATCCodesAndName(rm, thisMedication, medicationSettings));
    if (filteringMeds.length === 0 || !thisInReturnMeds) {
      returnMeds.push(thisMedication); // Push first or only of its kind of medication to returnMeds
      return;
    }

    // Compare current medication to every other medication that have already been accepted
    const shouldNotBeFiltered = filteringMeds.every(filter => {
      // If both medications are from integration then we can show them both overlapping
      if (sourceIsIntegration(filter._source) && sourceIsIntegration(thisMedication._source)) return true;

      if (!thisMedication.hasEnded?.[0] && !filter.hasEnded?.[0]) {
        // Medication has not ended and filter not ended either. This automatically means that they have to overlap.
        return false;
      } else {
        const filterStart = partialDateToValue(filter.startDate);
        const filterEnd = filter.endDate ? partialDateToValue(filter.endDate) : Date.now();
        const medStart = partialDateToValue(thisMedication.startDate);
        const medEnd = partialDateToValue(thisMedication.endDate);
        if (medStart > filterStart && medStart < filterEnd) return false; // Medication start overlaps with filter
        if (medEnd > filterStart && medEnd < filterEnd) return false; // Medication end overlaps with filter
        if (medStart <= filterStart && medEnd >= filterEnd) return false; // Whole medication (being longer) overlaps with filter
        if (medStart >= filterStart && medEnd <= filterEnd) return false; // Whole medication (being shorter) overlaps with filter
        return true;
      }
    });

    shouldNotBeFiltered && returnMeds.push(thisMedication);
  });

  return returnMeds;
};

/**
 * Filter measurement documents
 * All platforms: Filter if measurement values coming from integration overlap with locally/manually entered ones
 * @param {INeuroDocument[]} documents - INeuroDocuments array
 * @returns {INeuroDocument[]} Filtered documents
 */
export const filterMeasurements = <T extends IControlProps>(
  docs: INeuroDocument[],
  sortedAndMergedDocuments: Array<T & any>,
): INeuroDocument[] => {
  const returnedDocuments = docs.filter(d => d.documentType !== 'measurement');
  const measurements = docs.filter(d => d.documentType === 'measurement');

  if (measurements.length === 0) return returnedDocuments;

  const mergedMeasurements = sortedAndMergedDocuments.filter(d => d._type === 'measurement') as IMeasurement[];

  const measurementFilter = (measurement: INeuroDocument): boolean => {
    const thisMeasurement = mergedMeasurements.find(m => m._id === measurement.documentId);

    if (!thisMeasurement) return true;

    const typeAndDateComparison = (d: IMeasurement) =>
      partialDateToValue(d.date) === partialDateToValue(thisMeasurement.date) &&
      fieldNameToCodeString(d.code ?? '') === fieldNameToCodeString(thisMeasurement.code ?? '');

    return !thisMeasurement._lockedFor || !mergedMeasurements.some(d => typeAndDateComparison(d) && !d._lockedFor);
  };

  return [...returnedDocuments, ...measurements.filter(measurementFilter)];
};

const includedLForceDocTypes = ['LAU', 'UTL']; // Lausuttu, ulkoinen lausunto

/**
 * Filter L-Force documents
 * @param {INeuroDocument[]} documents - INeuroDocuments array
 * @returns {INeuroDocument[]} Filtered documents
 */
export const filterLForceDocs = <T extends IControlProps>(
  documents: INeuroDocument[],
  sortedAndMergedDocuments: Array<T & any>,
): INeuroDocument[] => {
  const filteredDocuments = documents.filter(d => {
    if (d.source === 'l-force-integration') {
      const mergedDocument: { studyStatus: string } = sortedAndMergedDocuments.find(s => s._id === d.documentId);
      // Only include wanted L-Force doc types
      if (includedLForceDocTypes.includes(mergedDocument.studyStatus)) {
        return true;
      }
      return false;
    }
    return true;
  });
  return filteredDocuments;
};
